import { CLOSING_DIALOG_SELECTOR } from "@src/utils/dialog-selectors";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type Politeness = "polite" | "assertive";

type Announce = (message: string, politeness?: Politeness) => void;

interface Sink {
  write: (politeness: Politeness, texts: string[]) => void;
  clear: () => void;
}

interface SinkRegistry {
  registerSink: (sink: Sink) => () => void;
  requestFlush: () => void;
}

interface Announcement {
  text: string;
  id: number;
}

interface PendingAnnouncement {
  text: string;
  politeness: Politeness;
}

const NO_ANNOUNCEMENTS: Announcement[] = [];

const LiveAnnouncerContext = createContext<Announce>(() => {});

const ClearAnnouncementsContext = createContext<() => void>(() => {});

const SinkRegistryContext = createContext<SinkRegistry>({
  registerSink: () => () => {},
  requestFlush: () => {},
});

function useRegionState(): [
  { polite: Announcement[]; assertive: Announcement[] },
  Sink,
] {
  const [polite, setPolite] = useState(NO_ANNOUNCEMENTS);
  const [assertive, setAssertive] = useState(NO_ANNOUNCEMENTS);
  const nextId = useRef(1);

  const sink = useMemo<Sink>(
    () => ({
      write: (politeness, texts) => {
        const announcements = texts.map((text) => ({
          text,
          id: nextId.current++,
        }));
        if (politeness === "assertive") {
          setAssertive(announcements);
          return;
        }
        setPolite(announcements);
      },
      clear: () => {
        setPolite(NO_ANNOUNCEMENTS);
        setAssertive(NO_ANNOUNCEMENTS);
      },
    }),
    [],
  );

  return [{ polite, assertive }, sink];
}

function Regions({
  polite,
  assertive,
}: {
  polite: Announcement[];
  assertive: Announcement[];
}): JSX.Element {
  return (
    <>
      <div role="status" aria-atomic="true" className="sr-only">
        {polite.map(({ id, text }) => (
          <span key={id}>{text}</span>
        ))}
      </div>
      <div role="alert" aria-atomic="true" className="sr-only">
        {assertive.map(({ id, text }) => (
          <span key={id}>{text}</span>
        ))}
      </div>
    </>
  );
}

export function LiveAnnouncerProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [regions, rootSink] = useRegionState();
  const sinks = useRef<Sink[]>([]);
  const pending = useRef<PendingAnnouncement[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    timer.current = null;
    if (document.querySelector(CLOSING_DIALOG_SELECTOR)) return;
    const batch = pending.current;
    pending.current = [];
    const target = sinks.current[sinks.current.length - 1] ?? rootSink;
    for (const politeness of ["polite", "assertive"] as const) {
      const texts = batch
        .filter((item) => item.politeness === politeness)
        .map((item) => item.text);
      if (texts.length > 0) target.write(politeness, texts);
    }
  }, [rootSink]);

  const announce = useCallback<Announce>(
    (message, politeness = "polite") => {
      const last = pending.current[pending.current.length - 1];
      if (last?.text === message && last.politeness === politeness) return;
      pending.current.push({ text: message, politeness });
      if (timer.current === null) timer.current = setTimeout(flush, 0);
    },
    [flush],
  );

  const clear = useCallback(() => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    pending.current = [];
    rootSink.clear();
    sinks.current.forEach((sink) => sink.clear());
  }, [rootSink]);

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  const registry = useMemo<SinkRegistry>(
    () => ({
      registerSink: (sink) => {
        if (!sinks.current.includes(sink)) sinks.current.push(sink);
        return () => {
          sinks.current = sinks.current.filter((existing) => existing !== sink);
        };
      },
      requestFlush: () => {
        if (pending.current.length > 0 && timer.current === null) {
          timer.current = setTimeout(flush, 0);
        }
      },
    }),
    [flush],
  );

  return (
    <SinkRegistryContext.Provider value={registry}>
      <ClearAnnouncementsContext.Provider value={clear}>
        <LiveAnnouncerContext.Provider value={announce}>
          {children}
          <Regions {...regions} />
        </LiveAnnouncerContext.Provider>
      </ClearAnnouncementsContext.Provider>
    </SinkRegistryContext.Provider>
  );
}

export function DialogLiveRegions({ open }: { open: boolean }): JSX.Element {
  const { registerSink, requestFlush } = useContext(SinkRegistryContext);
  const [regions, sink] = useRegionState();

  useLayoutEffect(() => requestFlush, [requestFlush]);

  useLayoutEffect(() => {
    if (!open) return;
    return registerSink(sink);
  }, [open, registerSink, sink]);

  return <Regions {...regions} />;
}

export function useAnnounce(): Announce {
  return useContext(LiveAnnouncerContext);
}

export function useClearAnnouncements(): () => void {
  return useContext(ClearAnnouncementsContext);
}
